// Customer Pulse Index — Premium Hospitality Intelligence (iter174 · PASS 2A)
// ─────────────────────────────────────────────────────────────────────────
// Trendline dashboards · sparklines · comparative analytics · heatmaps.
// Single backend call: GET /api/cx/analytics/trends — filters, comparison,
// and every chart series come back in one fast payload.
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  TrendingUp, TrendingDown, Minus, Flame,
  ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import CxDateRangeFilter, { loadSavedRange, resolveRange } from './CxDateRangeFilter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Severity palette — mirrors backend `severity` field 1:1.
const SEV = {
  excellent: { dot: '#2D6A4F', bg: 'bg-[#EEF4EF]', text: 'text-[#2D6A4F]', border: 'border-[#2D6A4F]/25', label: 'Excellent' },
  good:      { dot: '#52A57B', bg: 'bg-[#F1F7F2]', text: 'text-[#2D6A4F]', border: 'border-[#52A57B]/25', label: 'Healthy' },
  caution:   { dot: '#D89B5B', bg: 'bg-[#FEF6EC]', text: 'text-[#9C6815]', border: 'border-[#D89B5B]/30', label: 'Caution' },
  urgent:    { dot: '#C45A41', bg: 'bg-[#FCEDE7]', text: 'text-[#C45A41]', border: 'border-[#C45A41]/30', label: 'Urgent' },
  unknown:   { dot: '#9AAA9F', bg: 'bg-[#F9F9F8]', text: 'text-[#5C6B64]', border: 'border-[#E2E8DF]',     label: '—' },
};

const npsColor = (n) =>
  n == null ? '#9AAA9F' : n >= 60 ? '#2D6A4F' : n >= 30 ? '#52A57B'
    : n >= 0 ? '#D89B5B' : '#C45A41';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [, mm, dd] = iso.split('-');
  return `${dd}/${mm}`;
};

export default function CxAnalytics({ headers }) {
  // ─── Filters ──────────────────────────────────────────────────────
  // Date range is shared across tabs via localStorage so the manager's
  // window survives navigation (today/yesterday/last7/last30/this/prev month/custom).
  const [dateRange, setDateRange] = useState(() => {
    const saved = loadSavedRange();
    return { ...saved, resolved: resolveRange(saved.preset, saved.custom) };
  });
  const [filters, setFilters] = useState({
    category_id: 'all', area_id: 'all', sentiment: 'all', min_rating: 'all',
    compare_previous: true,
  });
  const [cats, setCats] = useState([]);
  const [areas, setAreas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load category + area lists for filter dropdowns once.
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cx/categories`, { headers }).then((r) => r.data).catch(() => []),
      axios.get(`${API}/cx/areas`, { headers }).then((r) => r.data).catch(() => []),
    ]).then(([c, a]) => { setCats(c); setAreas(a); });
  }, [headers]);

  const updateFilter = (k, v) => setFilters((p) => {
    const next = { ...p, [k]: v };
    // Reset area filter when category narrows.
    if (k === 'category_id') next.area_id = 'all';
    return next;
  });
  const resetFilters = () => {
    setFilters({
      category_id: 'all', area_id: 'all',
      sentiment: 'all', min_rating: 'all', compare_previous: true,
    });
    const saved = { preset: 'last_30_days', custom: {} };
    setDateRange({ ...saved, resolved: resolveRange(saved.preset, saved.custom) });
  };

  // ─── Build query string + fetch ───────────────────────────────────
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    const r = dateRange.resolved;
    params.set('start_date', r.start_date);
    params.set('end_date', r.end_date);
    params.set('days', String(r.days));  // legacy compatibility for max-window guard
    if (filters.category_id !== 'all') params.set('category_id', filters.category_id);
    if (filters.area_id !== 'all')     params.set('area_id', filters.area_id);
    if (filters.sentiment !== 'all')   params.set('sentiment', filters.sentiment);
    if (filters.min_rating !== 'all')  params.set('min_rating', String(filters.min_rating));
    if (filters.compare_previous)      params.set('compare_previous', 'true');
    return params.toString();
  }, [filters, dateRange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cx/analytics/trends?${queryString}`, { headers });
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || 'Failed to load analytics' });
    } finally { setLoading(false); }
  }, [queryString, headers]);

  useEffect(() => { load(); }, [load]);

  // Narrow areas list by category filter for the area dropdown.
  const visibleAreas = filters.category_id === 'all'
    ? areas
    : areas.filter((a) => a.category_id === filters.category_id);

  return (
    <div className="space-y-6" data-testid="cx-analytics">
      {/* ─── Header strip ────────────────────────────────────────── */}
      <div className="flex items-baseline justify-between flex-wrap gap-2 -mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B64] font-medium">
            {dateRange.resolved.start_date} → {dateRange.resolved.end_date} · {filters.compare_previous ? 'vs previous period' : 'no comparison'}
          </div>
          <h2 className="text-xl font-semibold text-[#1A2421] mt-0.5">Customer Pulse Intelligence</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CxDateRangeFilter value={dateRange} onChange={setDateRange} testId="cx-trends-daterange" />
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await axios.get(`${API}/cx/analytics/export.pdf?${queryString}`,
                { headers, responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
              const a = document.createElement('a'); a.href = url;
              a.download = `cx-experience-report-${new Date().toISOString().slice(0, 10)}.pdf`;
              a.click(); URL.revokeObjectURL(url);
            } catch { /* silent */ }
          }} data-testid="cx-trends-export-pdf">
            Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="cx-trends-reset">
            Reset filters
          </Button>
        </div>
      </div>

      {/* ─── Filter bar ──────────────────────────────────────────── */}
      <Card className="border-[#E2E8DF]">
        <CardContent className="p-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <FilterSelect label="Category" value={filters.category_id}
                          onChange={(v) => updateFilter('category_id', v)}
                          options={[['all','All categories'],
                                    ...cats.map((c) => [c.id, c.name])]}
                          testId="cx-trends-category" />
            <FilterSelect label="Area" value={filters.area_id}
                          onChange={(v) => updateFilter('area_id', v)}
                          options={[['all','All areas'],
                                    ...visibleAreas.map((a) => [a.id, a.name])]}
                          testId="cx-trends-area" />
            <FilterSelect label="Sentiment" value={filters.sentiment}
                          onChange={(v) => updateFilter('sentiment', v)}
                          options={[['all','Any sentiment'],['positive','Positive only'],
                                    ['neutral','Neutral only'],['negative','Negative only']]}
                          testId="cx-trends-sentiment" />
            <FilterSelect label="Min rating" value={filters.min_rating}
                          onChange={(v) => updateFilter('min_rating', v === 'all' ? 'all' : parseFloat(v))}
                          options={[['all','Any rating'],['1','★ 1+'],['2','★ 2+'],
                                    ['3','★ 3+'],['4','★ 4+'],['4.5','★ 4.5+']]}
                          testId="cx-trends-rating" />
            <div className="flex items-end h-full">
              <button type="button"
                onClick={() => updateFilter('compare_previous', !filters.compare_previous)}
                className={`w-full text-[11.5px] font-medium px-3 py-2 rounded-md border transition ${
                  filters.compare_previous
                    ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                    : 'bg-white text-[#1A2421] border-[#E2E8DF] hover:border-[color:var(--color-primary)]'
                }`}
                data-testid="cx-trends-compare-toggle">
                {filters.compare_previous ? '✓ Comparing' : 'Compare vs prev'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && !data && (
        <div className="text-[#5C6B64] py-12 text-center" data-testid="cx-trends-loading">
          Computing intelligence…
        </div>
      )}
      {data?.error && (
        <div className="bg-[#FCEDE7] border border-[#C45A41]/30 text-[#C45A41] p-4 rounded-md">
          {data.error}
        </div>
      )}

      {data && !data.error && (
        <>
          {/* ─── Attention Intelligence (must-act today) ─────────── */}
          {data.attention_intelligence?.length > 0 && (
            <Card className="border-[#C45A41]/30 bg-gradient-to-br from-[#FCEDE7]/50 to-white">
              <CardHeader className="pb-1.5">
                <CardTitle className="text-[13px] flex items-center gap-2">
                  <Flame className="w-4 h-4 text-[#C45A41]" /> Attention needed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {data.attention_intelligence.map((a, i) => {
                  const tok = SEV[a.severity] || SEV.caution;
                  return (
                    <div key={i}
                         className={`flex items-start gap-2 text-[12.5px] ${tok.text}`}
                         data-testid={`cx-attention-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: tok.dot }} />
                      <span className="flex-1">{a.message}</span>
                      <span className={`text-[9.5px] font-medium ${tok.bg} ${tok.text} px-1.5 py-0.5 rounded-full whitespace-nowrap`}>
                        {tok.label}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* ─── Comparison KPIs (current vs previous) ───────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cx-trends-kpis">
            <ComparisonKpi label="NPS"
              current={data.summary.nps} previous={data.comparison?.previous?.nps}
              delta={data.comparison?.delta?.nps} direction={data.comparison?.direction?.nps}
              format={(v) => v?.toFixed?.(1) ?? '—'} color={npsColor(data.summary.nps)}
              testId="cx-trends-kpi-nps" />
            <ComparisonKpi label="Avg Rating"
              current={data.summary.avg_rating} previous={data.comparison?.previous?.avg_rating}
              delta={data.comparison?.delta?.avg_rating} direction={data.comparison?.direction?.avg_rating}
              format={(v) => v?.toFixed?.(2) ?? '—'} suffix="/5"
              testId="cx-trends-kpi-rating" />
            <ComparisonKpi label="Positive Sentiment"
              current={data.summary.positive_pct} previous={data.comparison?.previous?.positive_pct}
              delta={data.comparison?.delta?.positive_pct} direction={data.comparison?.direction?.positive_pct}
              format={(v) => v != null ? `${v}%` : '—'}
              testId="cx-trends-kpi-positive" />
            <ComparisonKpi label="Responses"
              current={data.summary.submission_count} previous={data.comparison?.previous?.submission_count}
              delta={data.comparison?.delta?.submission_count}
              direction={(data.comparison?.delta?.submission_count || 0) > 0 ? 'improving' : (data.comparison?.delta?.submission_count || 0) < 0 ? 'declining' : 'flat'}
              format={(v) => v ?? '0'} integer
              testId="cx-trends-kpi-volume" />
            <ComparisonKpi label="Complaints"
              current={data.summary.complaint_count} previous={data.comparison?.previous?.complaint_count}
              delta={data.comparison?.delta?.complaint_count}
              direction={data.comparison?.direction?.complaint_count}
              format={(v) => v ?? '0'} integer invertedColors
              testId="cx-trends-kpi-complaints" />
          </div>

          {/* ─── Charts row 1 — NPS trend + Rating trend ─────────── */}
          <div className="grid lg:grid-cols-2 gap-4">
            <TrendCard
              title="NPS Trend"
              subtitle={`Daily NPS · 7-day rolling line · ${filters.compare_previous ? 'previous period overlay' : 'current only'}`}
              testId="cx-chart-nps-trend"
              chart={<NpsAreaChart
                series={data.nps_trend} prev={data.previous_period_series?.nps_trend}
                showPrev={filters.compare_previous} />}
            />
            <TrendCard
              title="Avg Rating Trend"
              subtitle="Daily average · 7-day rolling · scale 0–5"
              testId="cx-chart-rating-trend"
              chart={<RatingAreaChart series={data.rating_trend} prev={data.previous_period_series?.rating_trend}
                                       showPrev={filters.compare_previous} />}
            />
          </div>

          {/* ─── Charts row 2 — Sentiment movement + Volume ──────── */}
          <div className="grid lg:grid-cols-2 gap-4">
            <TrendCard
              title="Sentiment Movement"
              subtitle="Daily stacked split · positive / neutral / negative"
              testId="cx-chart-sentiment"
              chart={<SentimentStackChart series={data.sentiment_trend} />}
            />
            <TrendCard
              title="Response Volume"
              subtitle={`${data.summary.submission_count} responses in window`}
              testId="cx-chart-volume"
              chart={<VolumeBarChart series={data.volume_trend} prev={data.previous_period_series?.volume_trend}
                                     showPrev={filters.compare_previous} />}
            />
          </div>

          {/* ─── Complaint frequency trend ───────────────────────── */}
          <TrendCard
            title="Complaint Frequency"
            subtitle="Daily complaint count · % of total volume"
            testId="cx-chart-complaints"
            chart={<ComplaintTrendChart series={data.complaint_trend} />}
          />

          {/* ─── Department comparison ───────────────────────────── */}
          <Card className="border-[#E2E8DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[14px]">Department Comparison</CardTitle>
              <div className="text-[11px] text-[#5C6B64] mt-0.5">
                Ranked by NPS · 14-day pulse sparkline · click to filter
              </div>
            </CardHeader>
            <CardContent>
              {data.department_comparison.length === 0 ? (
                <div className="text-[12.5px] text-[#5C6B64] py-6 text-center">No category data yet.</div>
              ) : (
                <div className="space-y-2" data-testid="cx-dept-comparison">
                  {data.department_comparison.map((d) => (
                    <DepartmentRow key={d.category_id} dept={d}
                      onClick={() => updateFilter('category_id', d.category_id)} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ─── Improving vs Declining areas ────────────────────── */}
          {filters.compare_previous && (
            <div className="grid md:grid-cols-2 gap-4">
              <MovementCard title="Top Improving Areas"
                icon={<TrendingUp className="w-4 h-4 text-[#2D6A4F]" />}
                color="#2D6A4F" items={data.area_movements.improving}
                emptyText="No notable improvers vs previous period."
                testId="cx-movers-improving" />
              <MovementCard title="Top Declining Areas"
                icon={<TrendingDown className="w-4 h-4 text-[#C45A41]" />}
                color="#C45A41" items={data.area_movements.declining}
                emptyText="✓ No declining areas vs previous period."
                testId="cx-movers-declining" />
            </div>
          )}

          {/* ─── Heatmap ─────────────────────────────────────────── */}
          <Card className="border-[#E2E8DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[14px]">Operational Heatmap</CardTitle>
              <div className="text-[11px] text-[#5C6B64] mt-0.5">
                Area × complaint theme · darker = more mentions · top {Math.min(10, data.heatmap.rows.length)} rows
              </div>
            </CardHeader>
            <CardContent>
              <HeatmapGrid heatmap={data.heatmap} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Sub-components                                                       */
/* ──────────────────────────────────────────────────────────────────── */

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-0">
      <Label className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium block mb-1">{label}</Label>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-[12.5px]" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={String(v)} value={String(v)} className="text-[12.5px]">{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ComparisonKpi({ label, current, previous, delta, direction, format, suffix, color, integer, invertedColors, testId }) {
  // "invertedColors": for complaints, going DOWN is good.
  const dir = direction || 'flat';
  const ok = invertedColors ? dir === 'declining' : dir === 'improving';
  const bad = invertedColors ? dir === 'improving' : dir === 'declining';
  const deltaColor = ok ? '#2D6A4F' : bad ? '#C45A41' : '#5C6B64';
  const Icon = ok ? ArrowUpRight : bad ? ArrowDownRight : Minus;
  const arrowSign = invertedColors ? -1 : 1;
  const sgn = (delta != null && delta !== 0)
    ? ((delta * arrowSign) > 0 ? '+' : '')
    : '';
  return (
    <Card className="border-[#E2E8DF] relative overflow-hidden" data-testid={testId}>
      {color && <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />}
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium">{label}</div>
        <div className="flex items-baseline gap-1 mt-1.5">
          <span className="text-[28px] font-semibold tabular-nums leading-tight" style={{ color: color || '#1A2421' }}>
            {format(current)}
          </span>
          {suffix && <span className="text-[11px] text-[#5C6B64]">{suffix}</span>}
        </div>
        {delta != null && previous != null ? (
          <div className="flex items-center gap-1 text-[11px] mt-1.5" style={{ color: deltaColor }}>
            <Icon className="w-3 h-3" />
            <span className="font-medium tabular-nums">{sgn}{integer ? delta : delta?.toFixed?.(2)}</span>
            <span className="text-[10.5px] text-[#5C6B64]">vs {integer ? previous : format(previous)} prev</span>
          </div>
        ) : (
          <div className="text-[10.5px] text-[#5C6B64] mt-1.5">no prior data</div>
        )}
      </CardContent>
    </Card>
  );
}

function TrendCard({ title, subtitle, chart, testId }) {
  return (
    <Card className="border-[#E2E8DF]" data-testid={testId}>
      <CardHeader className="pb-1.5">
        <CardTitle className="text-[14px]">{title}</CardTitle>
        {subtitle && <div className="text-[11px] text-[#5C6B64] mt-0.5">{subtitle}</div>}
      </CardHeader>
      <CardContent className="pt-0">{chart}</CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Charts (recharts wrappers — minimal config, premium styling)         */
/* ──────────────────────────────────────────────────────────────────── */

function ChartTooltip({ active, payload, label, suffix = '', integer = false }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-[#E2E8DF] rounded-md shadow-md px-3 py-2 text-[11.5px]">
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular-nums">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#1A2421]">{p.name}</span>
          <span className="font-medium ml-auto">
            {p.value == null ? '—' : integer ? p.value : Number(p.value).toFixed(2)}{suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

function NpsAreaChart({ series, prev, showPrev }) {
  // Align previous series by index (so charts overlay even if dates differ).
  const merged = series.map((d, i) => ({
    ...d, prev_nps: showPrev && prev ? prev[i]?.nps ?? null : null,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={merged} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="cx-nps-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2D6A4F" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#2D6A4F" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8DF" />
        <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={{ stroke: '#E2E8DF' }} interval="preserveStartEnd" />
        <YAxis domain={[-100, 100]} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={false} width={44}
               ticks={[-100, -50, 0, 50, 100]} />
        <ReferenceLine y={0} stroke="#9AAA9F" strokeDasharray="3 3" />
        <ReferenceLine y={50} stroke="#2D6A4F" strokeDasharray="2 4" strokeOpacity={0.4} />
        <Tooltip content={<ChartTooltip />} />
        {showPrev && prev && (
          <Line dataKey="prev_nps" name="Previous" stroke="#9AAA9F"
                strokeWidth={1.5} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
        )}
        <Area dataKey="nps" name="Daily NPS" stroke="#2D6A4F" fill="url(#cx-nps-grad)"
              strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
        <Line dataKey="rolling7" name="7-day rolling" stroke="var(--color-primary)" strokeWidth={2.5} dot={false}
              isAnimationActive={false} connectNulls />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function RatingAreaChart({ series, prev, showPrev }) {
  const merged = series.map((d, i) => ({
    ...d, prev_rating: showPrev && prev ? prev[i]?.rating ?? null : null,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={merged} margin={{ top: 6, right: 6, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="cx-rate-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#52A57B" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#52A57B" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8DF" />
        <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={{ stroke: '#E2E8DF' }} interval="preserveStartEnd" />
        <YAxis domain={[0, 5]} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={false} width={36}
               ticks={[0, 1, 2, 3, 4, 5]} />
        <ReferenceLine y={4} stroke="#2D6A4F" strokeDasharray="2 4" strokeOpacity={0.4} />
        <Tooltip content={<ChartTooltip />} />
        {showPrev && prev && (
          <Line dataKey="prev_rating" name="Previous" stroke="#9AAA9F"
                strokeWidth={1.5} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
        )}
        <Area dataKey="rating" name="Daily ★" stroke="#52A57B" fill="url(#cx-rate-grad)"
              strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
        <Line dataKey="rolling7" name="7-day rolling" stroke="var(--color-primary)" strokeWidth={2.5} dot={false}
              isAnimationActive={false} connectNulls />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function SentimentStackChart({ series }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={series} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8DF" />
        <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={{ stroke: '#E2E8DF' }} interval="preserveStartEnd" />
        <YAxis fontSize={10} stroke="#5C6B64" tickLine={false} axisLine={false} width={36} />
        <Tooltip content={<ChartTooltip integer />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="positive" name="Positive" stackId="a" fill="#2D6A4F" radius={[2, 2, 0, 0]} />
        <Bar dataKey="neutral"  name="Neutral"  stackId="a" fill="#D89B5B" />
        <Bar dataKey="negative" name="Negative" stackId="a" fill="#C45A41" radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function VolumeBarChart({ series, prev, showPrev }) {
  const merged = series.map((d, i) => ({
    ...d, prev_count: showPrev && prev ? prev[i]?.count ?? null : null,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={merged} margin={{ top: 6, right: 6, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8DF" />
        <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={{ stroke: '#E2E8DF' }} interval="preserveStartEnd" />
        <YAxis fontSize={10} stroke="#5C6B64" tickLine={false} axisLine={false} width={36} />
        <Tooltip content={<ChartTooltip integer />} />
        {showPrev && prev && (
          <Bar dataKey="prev_count" name="Previous" fill="#9AAA9F" fillOpacity={0.45} radius={[2, 2, 0, 0]} />
        )}
        <Bar dataKey="count" name="Responses" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ComplaintTrendChart({ series }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={series} margin={{ top: 6, right: 24, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="cx-cmp-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C45A41" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#C45A41" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="#E2E8DF" />
        <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={{ stroke: '#E2E8DF' }} interval="preserveStartEnd" />
        <YAxis yAxisId="left" fontSize={10} stroke="#5C6B64" tickLine={false} axisLine={false} width={36} />
        <YAxis yAxisId="right" orientation="right" fontSize={10} stroke="#5C6B64"
               tickLine={false} axisLine={false} width={32}
               tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
        <Tooltip content={<ChartTooltip integer />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area yAxisId="left" dataKey="complaints" name="Complaints" stroke="#C45A41"
              fill="url(#cx-cmp-grad)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
        <Line yAxisId="right" dataKey="complaint_rate_pct" name="Rate %" stroke="#D89B5B"
              strokeWidth={2} dot={false} strokeDasharray="4 3" isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Department row + Movement card + Heatmap                             */
/* ──────────────────────────────────────────────────────────────────── */

function DepartmentRow({ dept, onClick }) {
  const tok = SEV[dept.severity] || SEV.unknown;
  return (
    <button type="button" onClick={onClick}
      className={`w-full rounded-md border ${tok.border} bg-white hover:shadow-sm hover:-translate-y-px transition-all p-3 text-left`}
      data-testid={`cx-dept-${dept.category_id}`}>
      <div className="flex items-center gap-3">
        <span className={`w-2 h-2 rounded-full shrink-0`} style={{ background: tok.dot }} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-[#1A2421] truncate">{dept.category_name}</div>
          <div className="text-[10.5px] text-[#5C6B64]">{dept.submission_count} response{dept.submission_count === 1 ? '' : 's'}</div>
        </div>
        <div className="hidden sm:block w-24 h-8 shrink-0">
          <Sparkline values={dept.sparkline} stroke={npsColor(dept.nps)} />
        </div>
        <div className="grid grid-cols-3 gap-3 text-right text-[11.5px] tabular-nums shrink-0">
          <div>
            <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">NPS</div>
            <div className="font-semibold" style={{ color: npsColor(dept.nps) }}>
              {dept.nps != null ? dept.nps : '—'}
            </div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">★</div>
            <div className="font-semibold text-[#1A2421]">
              {dept.avg_rating != null ? dept.avg_rating.toFixed(1) : '—'}
            </div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">+%</div>
            <div className="font-semibold text-[#1A2421]">
              {dept.submission_count
                ? Math.round(100 * (dept.sentiment_split?.positive || 0) / dept.submission_count)
                : 0}%
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function Sparkline({ values = [], stroke = '#2D6A4F', height = 28 }) {
  // Tiny inline SVG sparkline. Renders nulls as gaps.
  const W = 96, H = height;
  const nums = values.filter((v) => v != null);
  if (!nums.length) {
    return <div className="text-[9.5px] text-[#5C6B64] italic h-full flex items-end">no data</div>;
  }
  const min = Math.min(...nums, 0);
  const max = Math.max(...nums, 10);
  const range = Math.max(1, max - min);
  const step = W / Math.max(1, values.length - 1);
  let d = '';
  let started = false;
  values.forEach((v, i) => {
    if (v == null) { started = false; return; }
    const x = i * step;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    d += `${started ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)} `;
    started = true;
  });
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <path d={d.trim()} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function MovementCard({ title, icon, color, items, emptyText, testId }) {
  return (
    <Card className="border-[#E2E8DF]" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-[14px] flex items-center gap-2">
          {icon} {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!items?.length ? (
          <div className="text-[12px] text-[#5C6B64] italic py-3">{emptyText}</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((m) => (
              <div key={m.area_id} className="flex items-center gap-3 text-[12.5px]"
                   data-testid={`cx-mover-${m.area_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[#1A2421] font-medium truncate">{m.area_name}</div>
                  <div className="text-[10.5px] text-[#5C6B64] truncate">
                    {m.category_name || '—'} · {m.current_volume} responses
                  </div>
                </div>
                <div className="text-right shrink-0 tabular-nums">
                  <div className="font-semibold" style={{ color }}>
                    {m.delta > 0 ? '+' : ''}{m.delta} NPS
                  </div>
                  <div className="text-[10px] text-[#5C6B64]">
                    {m.previous_nps ?? '—'} → {m.current_nps ?? '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HeatmapGrid({ heatmap }) {
  const { themes, rows, peak } = heatmap;
  const visible = rows.slice(0, 10);
  if (!visible.length) {
    return <div className="text-[12.5px] text-[#5C6B64] py-6 text-center">No complaint mentions yet. Healthy ✓</div>;
  }
  const cellColor = (n) => {
    if (!n) return '#F7F8F9';
    const intensity = peak > 0 ? n / peak : 0;
    // Stepped gradient: cream → amber → red.
    if (intensity >= 0.75) return '#C45A41';
    if (intensity >= 0.50) return '#E37856';
    if (intensity >= 0.25) return '#F0A777';
    return '#FBD7B5';
  };
  const cellText = (n) => (n && peak > 0 && (n / peak) >= 0.5 ? 'white' : '#1A2421');
  return (
    <div className="overflow-x-auto" data-testid="cx-heatmap">
      <table className="w-full border-separate" style={{ borderSpacing: '2px' }}>
        <thead>
          <tr>
            <th className="text-left text-[10.5px] uppercase tracking-wider text-[#5C6B64] font-medium pb-1.5">
              Area
            </th>
            {themes.map((t) => (
              <th key={t} className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium pb-1.5 px-1 text-center whitespace-nowrap">
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.area_id} data-testid={`cx-heatmap-row-${row.area_id}`}>
              <td className="text-[12px] text-[#1A2421] pr-2 max-w-[180px]">
                <div className="font-medium truncate">{row.area_name}</div>
                <div className="text-[10px] text-[#5C6B64] truncate">{row.category_name || '—'}</div>
              </td>
              {themes.map((t) => {
                const n = row.themes[t] || 0;
                return (
                  <td key={t} className="text-center align-middle">
                    <div className="mx-auto rounded-md flex items-center justify-center text-[11.5px] font-semibold tabular-nums"
                         style={{
                           background: cellColor(n),
                           color: cellText(n),
                           width: 34, height: 34, minWidth: 34,
                         }}
                         title={`${row.area_name} · ${t}: ${n} mention${n === 1 ? '' : 's'}`}>
                      {n || ''}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 mt-3 text-[10.5px] text-[#5C6B64]">
        <span>Intensity:</span>
        {[
          ['#F7F8F9', '0'],
          ['#FBD7B5', 'low'],
          ['#F0A777', 'med'],
          ['#E37856', 'high'],
          ['#C45A41', 'peak'],
        ].map(([c, l]) => (
          <span key={l} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c }} /> {l}
          </span>
        ))}
        <span className="ml-auto">peak = {peak} mention{peak === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
}
